import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import NatalChartForm from '../components/natal/NatalChartForm';
import NatalChartResult from '../components/natal/NatalChartResult';
import { calculateNatalChart, type HebrewNatalData } from '../utils/hebrewCalendar';
import { api } from '../services/api';

type Stage = 'form' | 'result';

export default function NatalChartPage() {
  const [stage, setStage] = useState<Stage>('form');
  const [isCalculating, setIsCalculating] = useState(false);
  const [natalData, setNatalData] = useState<HebrewNatalData | null>(null);
  const [userName, setUserName] = useState('');
  const [interpretation, setInterpretation] = useState<string | null>(null);
  const [isLoadingInterpretation, setIsLoadingInterpretation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormSubmit = async (formData: {
    birthDate: string;
    birthTime: string;
    birthPlace: string;
    name: string;
  }) => {
    setIsCalculating(true);
    setError(null);
    try {
      // Calculate Hebrew calendar data (pure frontend — instant)
      const data = calculateNatalChart(formData.birthDate, formData.birthTime, formData.birthPlace);
      setNatalData(data);
      setUserName(formData.name);
      setStage('result');
    } catch (err) {
      setError('Error al calcular la fecha hebrea. Verifica los datos ingresados.');
      console.error(err);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleRequestInterpretation = async () => {
    if (!natalData) return;
    setIsLoadingInterpretation(true);
    try {
      const text = await api.interpretNatalChart({
        name: userName || undefined,
        birthDate: natalData.gregorianDate,
        birthTime: natalData.birthTime,
        birthPlace: natalData.birthPlace,
        hebrewDate: {
          year: natalData.hebrewYear,
          month: natalData.hebrewMonth,
          day: natalData.hebrewDay,
          monthName: natalData.hebrewMonthName.spanish,
        },
        mazal: {
          hebrewName: natalData.mazal.hebrewName,
          transliteration: natalData.mazal.transliteration,
          spanishName: natalData.mazal.spanishName,
        },
        tribe: {
          name: natalData.mazal.tribe.name,
          hebrew: natalData.mazal.tribe.hebrew,
        },
        letter: {
          letter: natalData.mazal.letter.letter,
          name: natalData.mazal.letter.name,
          meaning: natalData.mazal.letter.meaning,
          spiritualForce: natalData.mazal.letter.spiritualForce,
        },
        sefirah: {
          name: natalData.mazal.sefirah.name,
          hebrew: natalData.mazal.sefirah.hebrew,
          meaning: natalData.mazal.sefirah.meaning,
          qualities: natalData.mazal.sefirah.qualities,
        },
        tikun: natalData.mazal.tikun,
        qualities: natalData.mazal.qualities,
        element: natalData.mazal.element,
        planet: natalData.mazal.planet.name,
      });
      setInterpretation(text);
    } catch (err) {
      setInterpretation(
        'No fue posible conectar con el servicio de interpretación en este momento. ' +
        'Tu carta natal está completa — la interpretación AI requiere conexión con el backend.'
      );
    } finally {
      setIsLoadingInterpretation(false);
    }
  };

  const handleReset = () => {
    setStage('form');
    setNatalData(null);
    setInterpretation(null);
    setUserName('');
    setError(null);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <Link
          to="/"
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Inicio
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
            זִיווּג
          </span>
          <span className="text-slate-600 text-sm">· Carta natal hebrea</span>
        </div>
        <div className="w-20" /> {/* spacer */}
      </nav>

      {/* Main */}
      <main className="flex-1 flex items-start justify-center px-4 py-10">
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl z-50 max-w-sm text-center"
          >
            {error}
          </motion.div>
        )}

        {stage === 'form' && (
          <NatalChartForm onSubmit={handleFormSubmit} isLoading={isCalculating} />
        )}

        {stage === 'result' && natalData && (
          <NatalChartResult
            data={natalData}
            userName={userName}
            interpretation={interpretation}
            isLoadingInterpretation={isLoadingInterpretation}
            onRequestInterpretation={handleRequestInterpretation}
            onReset={handleReset}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-slate-700 text-xs border-t border-white/5">
        זִיווּג · Sistema de carta natal hebrea basado en el Séfer Yetzirá y la tradición cabalística
      </footer>
    </div>
  );
}
